// src/gestion-logistics/components/JobCostingLineFormModal.tsx
import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  message,
} from "antd";
import {
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
} from "@ant-design/icons";
import { useJobCosting } from "../hooks/useJobCosting";
import { useAccountMasters } from "../hooks/useAccountMasters";
import { useSelection } from "../../SelectionContext";
import {
  JobCostingLine,
  JobCostingFormData,
  CostLineType,
  AccountTypeCode,
} from "../types/costing.types";
import SupplierSelector from "./SupplierSelector";
import ClientSelector from "./ClientSelector";

const { Option } = Select;
const { TextArea } = Input;

interface JobCostingLineFormModalProps {
  visible: boolean;
  onClose: () => void;
  shipmentId: number;
  line?: JobCostingLine | null;
  mode: "create" | "edit";
}

/**
 * Modal de formulaire pour ajouter/éditer une ligne de job costing
 */
const JobCostingLineFormModal: React.FC<JobCostingLineFormModalProps> = ({
  visible,
  onClose,
  shipmentId,
  line,
  mode,
}) => {
  const [form] = Form.useForm();
  const { createJobCostingLine, updateJobCostingLine } = useJobCosting();
  const { accountMasters, accountTypes } = useAccountMasters();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const [loading, setLoading] = React.useState(false);
  const [selectedLineType, setSelectedLineType] = React.useState<CostLineType>(
    CostLineType.COST
  );

  useEffect(() => {
    if (visible && line && mode === "edit") {
      form.setFieldsValue({
        ...line,
      });
      setSelectedLineType(line.line_type);
    } else if (visible && mode === "create") {
      form.resetFields();
      form.setFieldsValue({
        shipment_id: shipmentId,
        line_type: CostLineType.COST,
        quantity: 1,
      });
      setSelectedLineType(CostLineType.COST);
    }
  }, [visible, line, mode, form, shipmentId]);

  const handleLineTypeChange = (value: CostLineType) => {
    setSelectedLineType(value);
    // Clear account master selection when line type changes
    form.setFieldValue("account_master_id", undefined);
    form.setFieldValue("label_name", "");
  };

  const handleAccountMasterChange = (value: number) => {
    const selectedAccount = accountMasters.find((am) => am.id === value);
    if (selectedAccount) {
      form.setFieldsValue({
        label_name: selectedAccount.name,
        unit: selectedAccount.default_unit || undefined,
        unit_price: selectedAccount.default_unit_price || undefined,
      });
      // Calculer le montant si on a quantity et unit_price
      const quantity = form.getFieldValue("quantity") || 1;
      const unitPrice = selectedAccount.default_unit_price || 0;
      if (unitPrice > 0) {
        form.setFieldValue("amount", quantity * unitPrice);
      }
    }
  };

  const handleQuantityOrPriceChange = () => {
    const quantity = form.getFieldValue("quantity") || 0;
    const unitPrice = form.getFieldValue("unit_price") || 0;
    const amount = quantity * unitPrice;
    form.setFieldValue("amount", amount);
  };

  const handleSupplierChange = (supplierId: number, supplierName: string) => {
    form.setFieldsValue({
      supplier_id: supplierId,
      supplier_name: supplierName,
    });
  };

  const handleClientChange = (clientId: number, clientName: string) => {
    form.setFieldsValue({
      client_id: clientId,
      client_name: clientName,
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Calculer le montant si pas déjà calculé
      if (!values.amount && values.quantity && values.unit_price) {
        values.amount = values.quantity * values.unit_price;
      }

      const formData: JobCostingFormData = {
        shipment_id: shipmentId,
        line_type: values.line_type,
        account_master_id: values.account_master_id,
        label_name: values.label_name,
        quantity: values.quantity,
        unit: values.unit,
        unit_price: values.unit_price || 0,
        supplier_id: values.supplier_id,
        supplier_name: values.supplier_name,
        supplier_reference: values.supplier_reference,
        client_id: values.client_id,
        client_name: values.client_name,
        client_reference: values.client_reference,
        description: values.description,
      };

      let success = false;

      if (mode === "create") {
        const result = await createJobCostingLine(formData);
        success = result !== null;
      } else if (line) {
        success = await updateJobCostingLine(line.id, formData);
      }

      if (success) {
        form.resetFields();
        onClose();
      }
    } catch (error) {
      console.error("[LOGISTICS] Form validation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les account masters selon le line_type sélectionné
  const filteredAccountMasters = accountMasters.filter((am) => {
    if (selectedLineType === CostLineType.REVENUE) {
      // Pour les revenus, on veut uniquement les INCOME
      return am.account_type_code === AccountTypeCode.INCOME;
    } else if (selectedLineType === CostLineType.DISBURSEMENT) {
      // Pour les débours, on veut uniquement les DISBURSEMENT
      return am.account_type_code === AccountTypeCode.DISBURSEMENT;
    } else {
      // Pour les coûts, on veut uniquement les EXPENSE
      return am.account_type_code === AccountTypeCode.EXPENSE;
    }
  });

  return (
    <Modal
      title={
        <span>
          {selectedLineType === CostLineType.COST ? (
            <FallOutlined style={{ marginRight: 8, color: "#d46b08" }} />
          ) : selectedLineType === CostLineType.DISBURSEMENT ? (
            <DollarOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          ) : (
            <RiseOutlined style={{ marginRight: 8, color: "#52c41a" }} />
          )}
          {mode === "create" ? "Nouvelle Ligne" : "Modifier la Ligne"} -{" "}
          {selectedLineType === CostLineType.COST
            ? "Coût"
            : selectedLineType === CostLineType.DISBURSEMENT
            ? "Débours"
            : "Revenu"}
        </span>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={700}
      okText={mode === "create" ? "Ajouter" : "Enregistrer"}
      cancelText="Annuler"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        {/* Type de ligne */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Type de Ligne"
              name="line_type"
              rules={[{ required: true }]}
            >
              <Select onChange={handleLineTypeChange}>
                <Option value={CostLineType.COST}>
                  <FallOutlined style={{ color: "#d46b08" }} /> Coût (Expense)
                </Option>
                <Option value={CostLineType.DISBURSEMENT}>
                  <DollarOutlined style={{ color: "#1890ff" }} /> Débours (Disbursement)
                </Option>
                <Option value={CostLineType.REVENUE}>
                  <RiseOutlined style={{ color: "#52c41a" }} /> Revenu (Income)
                </Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Rubrique Comptable"
              name="account_master_id"
              tooltip="Sélectionnez une rubrique prédéfinie"
            >
              <Select
                placeholder="Sélectionner une rubrique"
                onChange={handleAccountMasterChange}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = option?.children?.toString() || "";
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {filteredAccountMasters.map((am) => (
                  <Option key={am.id} value={am.id}>
                    {am.code} - {am.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Libellé */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Libellé"
              name="label_name"
              rules={[{ required: true, message: "Le libellé est requis" }]}
            >
              <Input placeholder="Ex: Fret maritime, Assurance..." />
            </Form.Item>
          </Col>
        </Row>

        {/* Quantité, Unité, Prix unitaire */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Quantité" name="quantity">
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={0.01}
                placeholder="1.00"
                onChange={handleQuantityOrPriceChange}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="Unité" name="unit">
              <Input placeholder="Ex: container, kg, m³" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="Prix Unitaire"
              name="unit_price"
              rules={[{ required: true, message: "Prix requis" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={0.01}
                placeholder="0.00"
                onChange={handleQuantityOrPriceChange}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Montant calculé (affiché mais non éditable directement) */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Montant Total" name="amount">
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={0.01}
                placeholder="Calculé automatiquement"
                disabled
                prefix={<DollarOutlined />}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Fournisseur (pour les coûts et débours) */}
        {(selectedLineType === CostLineType.COST ||
          selectedLineType === CostLineType.DISBURSEMENT) && (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Fournisseur" name="supplier_id">
                  <SupplierSelector
                    onChange={handleSupplierChange}
                    companyId={selectedCompany}
                    warehouseId={selectedWarehouse}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Nom du Fournisseur"
                  name="supplier_name"
                  tooltip="Rempli automatiquement après sélection du fournisseur"
                >
                  <Input
                    placeholder="Sélectionnez un fournisseur"
                    disabled
                    style={{ cursor: 'not-allowed', backgroundColor: '#f5f5f5' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Référence Fournisseur" name="supplier_reference">
                  <Input placeholder="Ex: N° facture, devis..." />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {/* Client (uniquement pour les revenus) */}
        {selectedLineType === CostLineType.REVENUE && (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Client" name="client_id">
                  <ClientSelector
                    onChange={handleClientChange}
                    companyId={selectedCompany}
                    warehouseId={selectedWarehouse}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Nom du Client"
                  name="client_name"
                  tooltip="Rempli automatiquement après sélection du client"
                >
                  <Input
                    placeholder="Sélectionnez un client"
                    disabled
                    style={{ cursor: 'not-allowed', backgroundColor: '#f5f5f5' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Référence Client" name="client_reference">
                  <Input placeholder="Ex: Bon de commande, contrat..." />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {/* Description */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Description" name="description">
              <TextArea
                rows={2}
                placeholder="Détails supplémentaires..."
                maxLength={500}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default JobCostingLineFormModal;
